import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/SearchPanel.css";

interface SearchPanelProps {
  onSearch: (query: string) => void;
  isSearching: boolean;
  resetCounter?: number;
  onClear?: () => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  onSearch,
  isSearching,
  resetCounter,
  onClear,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const prevResetRef = useRef<number | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const suppressRef = useRef(false);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectionMade, setSelectionMade] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const prevIsSearchingRef = useRef<boolean>(isSearching);
  const selectionTimerRef = useRef<number | null>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError("Please enter a postcode or area");
      return;
    }
    setError(null);
    onSearch(searchQuery);
  };

  const handleClear = () => {
    setSearchQuery("");
    setError(null);
    setSuggestions([]);
    setHighlightedIndex(-1);
    suppressRef.current = false;
    setSelectionLoading(false);
    setSelectionMade(false);
    if (selectionTimerRef.current) {
      window.clearTimeout(selectionTimerRef.current);
      selectionTimerRef.current = null;
    }
    // notify parent to reset app-level search/selection if provided
    try {
      if (onClear) onClear();
    } catch (e) {}
  };

  // Clear internal input when parent signals reset
  useEffect(() => {
    if (typeof resetCounter === "undefined") return;
    if (prevResetRef.current === null) {
      prevResetRef.current = resetCounter;
      return;
    }
    if (resetCounter !== prevResetRef.current) {
      // parent requested reset
      setSearchQuery("");
      setError(null);
      setSelectionMade(false);
      prevResetRef.current = resetCounter;
    }
  }, [resetCounter]);

  // Ensure clicking outside the input blurs it (so it can be unfocused).
  // Avoid global re-focusing which prevented blur earlier.
  useEffect(() => {
    const onPointerDown = (ev: Event) => {
      try {
        const el = inputRef.current;
        if (!el) return;
        const target = ev.target as Node | null;
        if (!target) return;
        // If the pointer event originated inside the input, do nothing
        if (el === target || el.contains(target)) return;
        // If the input is currently focused, blur it so the user can unfocus
        if (document.activeElement === el) {
          try { el.blur(); } catch (e) {}
        }
      } catch (e) {}
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      try { document.removeEventListener('pointerdown', onPointerDown, true); } catch (e) {}
    };
  }, []);

  // Note: we intentionally avoid programmatic focusing here so clicking
  // the input is a purely user-driven action (no cursor on clear).

  const fetchSuggestions = useCallback((q: string) => {
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    // clear any previous pending fetch
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setSuggestionsLoading(true);
    // Restrict autocomplete suggestions to an approximate West Midlands bbox
    const westMidlandsBBox = {
      xmin: -2.6,
      ymin: 52.2,
      xmax: -1.0,
      ymax: 53.0,
    };

    // Geocode service base URL and optional API key are sourced from
    // environment variables to avoid embedding secrets in code and to
    // allow different deployments to target different geocode services.
    // `REACT_APP_GEOCODE_URL` should be set to the ArcGIS Geocode
    // service base (e.g. "https://.../GeocodeServer"). If it's not set
    // we fall back to the public WM Network GeocodeServer to preserve
    // development behaviour.
    const geocodeBase =
      process.env.REACT_APP_GEOCODE_URL ||
      "https://api.wmnetwork.co.uk/arcgis/rest/services/World/GeocodeServer";
    // Optional subscription/key header; only sent when provided.
    const geocodeKey = process.env.REACT_APP_GEOCODE_API_KEY || "";

    const url = `${geocodeBase}/findAddressCandidates?f=pjson&SingleLine=${encodeURIComponent(
      q,
    )}&countryCode=GB&searchExtent=${westMidlandsBBox.xmin},${westMidlandsBBox.ymin},${westMidlandsBBox.xmax},${westMidlandsBBox.ymax}`;

    const headers: Record<string, string> = {};
    if (geocodeKey) headers["Ocp-Apim-Subscription-Key"] = geocodeKey;

    fetch(url, {
      signal: controller.signal,
      headers,
    })
      .then((res) => res.json())
      .then((data) => {
        const cands = Array.isArray(data.candidates) ? data.candidates : [];
        setSuggestions(cands);
        setHighlightedIndex(-1);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Autocomplete fetch error", err);
        setSuggestions([]);
      })
      .finally(() => {
        setSuggestionsLoading(false);
      });
  }, []);

  // Debounce suggestion fetches
  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    if (suppressRef.current) {
      // a selection was just made; don't immediately re-query
      suppressRef.current = false;
      return;
    }
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }
    debounceRef.current = window.setTimeout(
      () => fetchSuggestions(searchQuery.trim()),
      300,
    );
    return () => {
      window.clearTimeout(debounceRef.current);
    };
  }, [searchQuery, fetchSuggestions]);

  const chooseSuggestion = (cand: any) => {
    const chosen =
      cand.address ||
      cand.attributes?.Match_addr ||
      cand.label ||
      cand.addressString ||
      "";
    setSearchQuery(chosen || "");
    setSuggestions([]);
    setHighlightedIndex(-1);
    // prevent immediate refetch and show loading while parent resolves location
    suppressRef.current = true;
    setSelectionLoading(true);
    setSelectionMade(true);
    // start a fallback timer in case parent doesn't set isSearching
    if (selectionTimerRef.current)
      window.clearTimeout(selectionTimerRef.current);
    selectionTimerRef.current = window.setTimeout(() => {
      setSelectionLoading(false);
      selectionTimerRef.current = null;
    }, 5000);
    onSearch(chosen || "");
  };

  // Clear selection loading when parent search completes (detect transition true -> false)
  useEffect(() => {
    if (
      prevIsSearchingRef.current === true &&
      isSearching === false &&
      selectionLoading
    ) {
      setSelectionLoading(false);
      if (selectionTimerRef.current) {
        window.clearTimeout(selectionTimerRef.current);
        selectionTimerRef.current = null;
      }
    }
    prevIsSearchingRef.current = isSearching;
    return () => {
      // cleanup on unmount
      if (selectionTimerRef.current) {
        window.clearTimeout(selectionTimerRef.current);
        selectionTimerRef.current = null;
      }
    };
  }, [isSearching, selectionLoading]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(-1, i - 1));
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        e.preventDefault();
        chooseSuggestion(suggestions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className="search-panel">
      <form
        onSubmit={handleSearch}
        className="search-form"
        aria-labelledby="search-panel-label"
      >
        <label id="search-panel-label" className="wmnds-screenreaders-only">
          Search by postcode or area
        </label>

        <div className="wmnds-grid">
          <div>
            <div className="wmnds-col-1">
              <h2 className="wmnds-h4">Enter a location</h2>
            </div>
            <div className="wmnds-autocomplete wmnds-grid">
              {!selectionMade && (
                <svg className="wmnds-autocomplete__icon">
                  <use
                    xlinkHref="#wmnds-general-search"
                    href="#wmnds-general-search"
                  ></use>
                </svg>
              )}
              {(suggestionsLoading || isSearching || selectionLoading) && (
                <div
                  className="wmnds-loader"
                  role="alert"
                  aria-live="assertive"
                >
                  <p className="wmnds-loader__content">Content is loading...</p>
                </div>
              )}
              {!selectionMade && (
                <input
                  className="wmnds-fe-input wmnds-autocomplete__input wmnds-col-1"
                  value={searchQuery}
                  placeholder="Postcode, street or area name"
                  name="autoCompleteSuggestions"
                  aria-label="Postcode, street or area name"
                  type="text"
                  id="autoCompleteSuggestions"
                  required={false}
                  autoComplete="off"
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={handleInputKeyDown}
                  disabled={isSearching}
                  aria-invalid={!!error}
                  aria-describedby={
                    error ? "postcode-error" : "postcode-helper"
                  }
                  ref={inputRef}
                  onPointerDown={() => {
                    try {
                      // Ensure the input receives focus when the user clicks/taps it
                      inputRef.current && inputRef.current.focus();
                    } catch (e) {}
                  }}
                />
              )}

              {selectionMade ? (
                <div className="wmnds-autocomplete__selected-item wmnds-grid wmnds-grid--justify-between wmnds-m-t-xs wmnds-m-b-md">
                  <strong className="wmnds-col-1 wmnds-autocomplete__selected-item-summary">
                    {searchQuery}
                  </strong>
                  <button
                    type="button"
                    className="wmnds-autocomplete__selected-item-close"
                    onClick={handleClear}
                    onPointerUp={() => { try { handleClear(); } catch(e){}; }}
                    onTouchEnd={() => { try { handleClear(); } catch(e){}; }}
                    aria-label="Clear search"
                  >
                    <svg className="wmnds-autocomplete__selected-item-close-icon" style={{ pointerEvents: 'none' }}>
                      <use
                        xlinkHref="#wmnds-general-cross"
                        href="#wmnds-general-cross"
                      ></use>
                    </svg>
                  </button>
                </div>
              ) : null}
              {suggestions && suggestions.length > 0 && (
                <ul
                  className="wmnds-autocomplete-suggestions"
                  aria-label="Search suggestions"
                >
                  {suggestions.map((s, idx) => {
                    const raw =
                      s.address ||
                      s.attributes?.Match_addr ||
                      s.label ||
                      s.addressString ||
                      "";
                    const title = raw;
                    const isHighlighted = idx === highlightedIndex;
                    const parts = [] as Array<React.ReactNode | string>;
                    const q = (searchQuery || "").trim();
                    if (q.length > 0) {
                      const re = new RegExp(
                        q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                        "ig",
                      );
                      let lastIndex = 0;
                      let match: RegExpExecArray | null;
                      while ((match = re.exec(raw)) !== null) {
                        if (match.index > lastIndex) {
                          parts.push(raw.substring(lastIndex, match.index));
                        }
                        parts.push(
                          <strong key={idx + "-" + match.index}>
                            {match[0]}
                          </strong>,
                        );
                        lastIndex = match.index + match[0].length;
                      }
                      if (lastIndex < raw.length)
                        parts.push(raw.substring(lastIndex));
                      if (parts.length === 0) parts.push(raw);
                    } else {
                      parts.push(raw);
                    }

                    return (
                      <li
                        key={`${raw}-${idx}`}
                        className={
                          "wmnds-autocomplete-suggestions__li wmnds-col-1" +
                          (isHighlighted ? " highlighted" : "")
                        }
                        title={title}
                        tabIndex={0}
                        role="option"
                        aria-selected={isHighlighted}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => chooseSuggestion(s)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            chooseSuggestion(s);
                          }
                        }}
                      >
                        {parts}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </form>

      <div id="postcode-helper" className="helper-text wmnds-col-1">
        Enter a postcode, street or area name. Example: B5 4BU or Edgbaston.
      </div>
      {error && (
        <div id="postcode-error" className="input-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default SearchPanel;
